import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MenuOpenRoundedIcon from "@mui/icons-material/MenuOpenRounded";
import MenuRoundedIcon from "@mui/icons-material/Menu";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

import classes from "./styles.module.scss";
import UpdatePassword from "../../orderTable/updatePassword";
import { getPermission } from "../../../shared/getPermission";

// Route → page title. Dynamic segments fall back to a prefix match so detail
// pages inherit the title of the list they came from.
const PAGE_TITLES = [
  ["/dashboard", "Dashboard"],
  ["/report", "Report"],
  ["/view-students", "Candidates"],
  ["/view-exams", "All Exams"],
  ["/bor-migrated", "BOR Migrated"],
  ["/waitlist", "Waitlist"],
  ["/certificates", "Certificates"],
  ["/subject", "Question Bank"],
  ["/topic", "Topics"],
  ["/question", "Questions"],
  ["/special-slots", "Special Slots"],
  ["/view-slots", "Slot Details"],
  ["/view-users", "New Users"],
  ["/view-cadre", "Cadre"],
  ["/subject-details", "Subject Details"],
  ["/change-password", "Change Password"],
  ["/form-details", "Candidate Form"],
  ["/user-details", "User Details"],
  ["/view-candidates", "Exam Candidates"],
  ["/view-candidate", "Legacy Candidate"],
  ["/feedback", "Feedback"],
];

const resolveTitle = (pathname) => {
  const match = PAGE_TITLES.find(
    ([path]) => pathname === path || pathname.startsWith(`${path}/`)
  );
  return match ? match[1] : "Admin Portal";
};

const TopBar = ({ collapsed, onToggleSidebar, onOpenMobileNav }) => {
  const [passwordModal, setPasswordModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [admin, setAdmin] = useState();
  const menuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem("bor-admin-login-data"));
    if (items) setAdmin(items);
  }, []);

  // Dismiss the account menu on an outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleClickAway = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickAway);
    return () => document.removeEventListener("mousedown", handleClickAway);
  }, [menuOpen]);

  const userType = admin?.data?.user_type;
  const username = admin?.data?.username;
  const initial = (username || userType || "A").charAt(0).toUpperCase();

  return (
    <header className={classes.topbar}>
      <div className={classes.left}>
        <button
          type="button"
          className={`${classes.icon_button} ${classes.desktop_only}`}
          onClick={onToggleSidebar}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <MenuRoundedIcon /> : <MenuOpenRoundedIcon />}
        </button>

        <button
          type="button"
          className={`${classes.icon_button} ${classes.mobile_only}`}
          onClick={onOpenMobileNav}
          aria-label="Open navigation"
        >
          <MenuRoundedIcon />
        </button>

        <div className={classes.titles}>
          <h1 className={classes.title}>{resolveTitle(location.pathname)}</h1>
          <span className={classes.breadcrumb}>
            {userType ? `${userType} Portal` : "Admin Portal"}
          </span>
        </div>
      </div>

      <div className={classes.right}>
        {getPermission("PASSWORD CHANGE") ? (
          <button
            type="button"
            className={classes.icon_button}
            onClick={() => setPasswordModal(true)}
            title="Update password"
            aria-label="Update password"
          >
            <KeyRoundedIcon />
          </button>
        ) : null}

        <div className={classes.account} ref={menuRef}>
          <button
            type="button"
            className={classes.account_button}
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <span className={classes.avatar}>{initial}</span>
            <span className={classes.account_text}>
              <span className={classes.account_name}>
                {username || "Administrator"}
              </span>
              <span className={classes.account_role}>{userType || "ADMIN"}</span>
            </span>
            <ExpandMoreRoundedIcon className={classes.account_chevron} />
          </button>

          {menuOpen && (
            <div className={classes.menu} role="menu">
              <div className={classes.menu_head}>
                <span className={classes.menu_name}>
                  {username || "Administrator"}
                </span>
                <span className={classes.menu_role}>{userType || "ADMIN"}</span>
              </div>

              {getPermission("PASSWORD CHANGE") ? (
                <button
                  type="button"
                  className={classes.menu_item}
                  onClick={() => {
                    setMenuOpen(false);
                    setPasswordModal(true);
                  }}
                >
                  <KeyRoundedIcon /> Update password
                </button>
              ) : null}

              <button
                type="button"
                className={`${classes.menu_item} ${classes.menu_item_danger}`}
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/logout");
                }}
              >
                <LogoutOutlinedIcon /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>

      {passwordModal && (
        <UpdatePassword
          open={passwordModal}
          close={() => setPasswordModal(false)}
        />
      )}
    </header>
  );
};

export default TopBar;
